import { useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "wouter";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Calendar, Calculator, BookOpen } from "lucide-react";
import type { BlogPost } from "@shared/schema";
import {
  getCategoryForPost,
  getCategoryBySlug,
  getRelatedArticles,
  getRelatedCalculators,
} from "@/lib/blog-categories";
import { applyDocumentMeta, truncateMetaDescription } from "@/lib/document-meta";

export default function BlogPostPage() {
  const params = useParams<{ slug: string }>();

  const { data: post, isLoading, error } = useQuery<BlogPost>({
    queryKey: ["/api/posts", params.slug],
    queryFn: async () => {
      const res = await fetch(`/api/posts/${params.slug}`);
      if (!res.ok) throw new Error("Post not found");
      return res.json();
    },
  });

  const { data: allPosts } = useQuery<BlogPost[]>({
    queryKey: ["/api/posts", "published"],
    queryFn: async () => {
      const res = await fetch("/api/posts?published=true");
      if (!res.ok) throw new Error("Failed to fetch posts");
      return res.json();
    },
    enabled: !!post,
  });

  const relatedArticles = useMemo(
    () => (allPosts && post ? getRelatedArticles(post.slug, allPosts, 4) : []),
    [allPosts, post]
  );

  const relatedCalculators = useMemo(
    () => (post ? getRelatedCalculators(post.slug, 2) : []),
    [post]
  );

  const categorySlug = post ? getCategoryForPost(post.slug) : null;
  const category = categorySlug ? getCategoryBySlug(categorySlug) : null;

  useEffect(() => {
    if (isLoading) return;
    if (error || !post) {
      applyDocumentMeta({
        title: "Post not found",
        description: "The article you're looking for doesn't exist or may have been removed.",
        noindex: true,
      });
      return;
    }
    applyDocumentMeta({
      title: post.title,
      description: truncateMetaDescription(post.excerpt || post.title),
    });
  }, [isLoading, error, post]);

  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen">
        <section className="py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <Link href="/blog">
              <Button variant="ghost" className="mb-8 text-muted-foreground hover:text-foreground -ml-4" data-testid="link-back-blog">
                <ArrowLeft size={16} className="mr-2" /> Back to Blog
              </Button>
            </Link>

            {isLoading ? (
              <div className="animate-pulse space-y-6">
                <div className="h-4 bg-white/5 rounded w-1/4" />
                <div className="h-12 bg-white/5 rounded w-3/4" />
                <div className="h-4 bg-white/5 rounded w-full" />
                <div className="h-4 bg-white/5 rounded w-full" />
                <div className="h-4 bg-white/5 rounded w-2/3" />
              </div>
            ) : error || !post ? (
              <div className="text-center py-20">
                <h2 className="text-2xl font-display font-bold mb-4">Post not found</h2>
                <p className="text-muted-foreground mb-8">The article you're looking for doesn't exist.</p>
                <Link href="/blog">
                  <Button>Back to Blog</Button>
                </Link>
              </div>
            ) : (
              <>
                <article>
                  <div className="flex items-center gap-3 mb-6">
                    {category && (
                      <Link href={`/blog/${categorySlug}`}>
                        <Badge variant="outline" className="border-primary/30 text-primary hover:bg-primary/10 cursor-pointer transition-colors" data-testid="badge-category">
                          {category.name}
                        </Badge>
                      </Link>
                    )}
                    <Calendar size={14} className="text-muted-foreground" aria-hidden="true" />
                    <time className="text-sm text-muted-foreground">
                      Last Updated: {new Date(post.updatedAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </time>
                  </div>

                  <h1 className="text-2xl sm:text-4xl md:text-5xl font-display font-bold mb-8 leading-tight" data-testid="text-post-title">
                    {post.title}
                  </h1>

                  {post.coverImage && (
                    <div className="rounded-2xl overflow-hidden border border-white/10 mb-10">
                      <img src={post.coverImage} alt={`Cover image for ${post.title}`} className="w-full h-auto" />
                    </div>
                  )}

                  <div
                    className="prose prose-invert prose-lg max-w-none
                      [&_p]:text-muted-foreground [&_p]:leading-relaxed [&_p]:mb-6
                      [&_h2]:text-foreground [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-12 [&_h2]:mb-4
                      [&_h3]:text-foreground [&_h3]:font-display [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-8 [&_h3]:mb-3
                      [&_ul]:text-muted-foreground [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6
                      [&_ol]:text-muted-foreground [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6
                      [&_li]:mb-2
                      [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4
                      [&_blockquote]:border-l-4 [&_blockquote]:border-primary/50 [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_blockquote]:my-8
                      [&_strong]:text-foreground [&_strong]:font-semibold
                      [&_code]:bg-white/5 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm
                    "
                    dangerouslySetInnerHTML={{ __html: post.content }}
                  />
                </article>

                {/* Related Content Section */}
                {(relatedArticles.length > 0 || relatedCalculators.length > 0) && (
                  <aside className="mt-16 pt-12 border-t border-white/10" aria-label="Related content">
                    <h2 className="text-2xl font-display font-bold mb-8">Related Content</h2>

                    {/* Related Calculators */}
                    {relatedCalculators.length > 0 && (
                      <div className="mb-8" aria-label="Related calculators">
                        <h3 className="text-sm font-medium text-primary uppercase tracking-wide mb-4 flex items-center gap-2">
                          <Calculator size={16} />
                          Related Calculators
                        </h3>
                        <div className="grid sm:grid-cols-2 gap-4">
                          {relatedCalculators.map((calc) => (
                            <Link key={calc.href} href={calc.href}>
                              <div
                                data-testid={`related-calc-${calc.href.split("/").pop()}`}
                                className="bg-card border border-white/10 rounded-xl p-5 hover:border-primary/40 transition-all group cursor-pointer"
                              >
                                <div className="flex items-start gap-3">
                                  <Calculator size={18} className="text-primary mt-0.5 shrink-0" />
                                  <div>
                                    <h4 className="font-display font-semibold group-hover:text-primary transition-colors">
                                      {calc.title}
                                    </h4>
                                    <p className="text-sm text-muted-foreground mt-1">{calc.description}</p>
                                  </div>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Related Articles */}
                    {relatedArticles.length > 0 && (
                      <div aria-label="Related articles">
                        <h3 className="text-sm font-medium text-primary uppercase tracking-wide mb-4 flex items-center gap-2">
                          <BookOpen size={16} />
                          Related Articles
                        </h3>
                        <div className="space-y-4">
                          {relatedArticles.map((related) => (
                            <Link key={related.id} href={`/blog/${related.slug}`}>
                              <div
                                data-testid={`related-article-${related.id}`}
                                className="bg-card border border-white/5 rounded-xl p-5 hover:border-primary/30 transition-all group cursor-pointer"
                              >
                                <h4 className="font-display font-semibold group-hover:text-primary transition-colors mb-1">
                                  {related.title}
                                </h4>
                                <p className="text-sm text-muted-foreground line-clamp-2">{related.excerpt}</p>
                                <span className="text-primary text-sm mt-2 flex items-center gap-1 group-hover:gap-2 transition-all">
                                  Read Article <ArrowRight size={14} />
                                </span>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Browse Category Link */}
                    {category && categorySlug && (
                      <div className="mt-8 text-center">
                        <Link href={`/blog/${categorySlug}`}>
                          <Button variant="outline" className="border-white/20 hover:border-primary/40" data-testid="link-browse-category">
                            Browse all {category.name} articles <ArrowRight size={14} className="ml-2" />
                          </Button>
                        </Link>
                      </div>
                    )}
                  </aside>
                )}
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
